import { IActionDataModel, IAdaptiveActionCard, adUnits } from '@ninjacart/nc-nk-core';
import {
  INinjaEvent,
  addEventParam,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import {
  KVPair,
  KissanEventType,
  UserProps,
  getKisaanBaseURL,
  toolId,
  useAppPersistenceStore,
  useFeedStore,
  useRecommendedProducts,
} from '@ninjacart/nc-nk-store';
import {
  AnimatePresence,
  Image,
  ImagePreview,
  Spinner,
  Stack,
  VideoPlayIconRegular,
  View,
  XStack,
  YStack,
} from '@ninjacart/nc-nk-ui';
import { BannerAds, TestIds, useRouter } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import {
  ForwardedRef,
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GestureResponderEvent, Platform } from 'react-native';
import { getVersion } from 'react-native-device-info';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { IHomeListItemRef } from '../../types/adaptiveInterface';
import { ListFooter } from './footer';
import { ListHeader } from './header';
import { ListTextContent } from './listTextContent';
import FeedVideoCard from './feedVideoCard';
import { RecommendedProductsNudgeComponent } from '../products/productNudge/recommendedProductNudge';
import { useTranslation } from 'react-i18next';
import { Link } from 'solito/build/link';

interface IHomeStreamListItem {
  item: IAdaptiveActionCard;
  index: number;
  userProfile?: UserProps;
  flashListRef?: FlashList<number> | null;
  screenDimensions: any;
  onReportBottomSheetOpen: Function;
  onCommentBottomSheetOpen: Function;
  pageName?: string;
  updateConnectionStatus?: Function;
  handleOnAutoPlayExpand?: (
    isExpanded: boolean,
    indexToExpand: number,
    videoId: number | string
  ) => void;
  adSizeDefsPreloaded?: KVPair;
  bannerAdKeywords?: string[];
  deviceWidth?: number;
  isAutoPlayBannerAdEnabled: boolean;
  setActiveFeedItemIndex?: (index: number) => void;
}

// const AutoPlayVideoLazyComponent = lazy(() =>
//   import('./feedVideoCard').then((module) => ({ default: module.default }))
// );

export const ListItem = forwardRef(
  (
    {
      item,
      index,
      userProfile,
      flashListRef,
      screenDimensions,
      onReportBottomSheetOpen,
      onCommentBottomSheetOpen,
      pageName,
      updateConnectionStatus,
      handleOnAutoPlayExpand,
      adSizeDefsPreloaded,
      bannerAdKeywords,
      deviceWidth,
      isAutoPlayBannerAdEnabled,
      setActiveFeedItemIndex,
    }: IHomeStreamListItem,
    ref: ForwardedRef<IHomeListItemRef>
  ) => {
    const videoExpandHeight = 250;
    const videoDefaultHeight = 200;

    // console.log("dgfhgfhfghfghfg => ", enableFeedItemClick);
    const lastItemId = useRef(item?.actionData?.content?.id);

    // const videoHeight = useMemo(
    //   () => {
    //     let height = deviceWidth ? (deviceWidth * 9) / 16 : videoDefaultHeight;
    //     return height > videoExpandHeight ? videoExpandHeight : height;
    //   },
    //   [deviceWidth, videoDefaultHeight, videoExpandHeight]
    // );

    const videoHeight = 250;

    const adUnitId = __DEV__
      ? TestIds.ADAPTIVE_BANNER
      : `/23198695442/kisan_app/homepage_active_feed_companion_ad`;
    const feedItemRef = useRef((item?.actionData as IActionDataModel)?.content?.id);

    const { actionData } = item;
    const { content } = actionData as IActionDataModel;
    const isRendered = useRef<boolean>(false);
    const [isVideoMuted, setIsVideoMuted] = useState<boolean>(
      useFeedStore.getState().feedVideoMuteEnable
    );
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 100;
    const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);
    const getActiveFeedItemIndex = useFeedStore((state) => state.getActiveFeedItemIndex);
    const activeFeedItemIndex = useFeedStore(
      useCallback((state) => getActiveFeedItemIndex(state), [index])
    );
    const isVideoFeedActive = activeFeedItemIndex === index;
    // console.log('isVideoFeedActive => ', isVideoFeedActive);
    // const [isVideoFeedActive, setIsVideoFeedActive] = useState(activeFeedItemIndex === index);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const currentInfoRef = useRef({ currentTime: 0 });
    const height = useSharedValue(videoHeight); // Default height
    const scale = useSharedValue(1);
    const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
    const adId = useAppPersistenceStore?.getState()?.adId;
    const isAdTrackingLimited = useAppPersistenceStore.getState().isAdTrackingLimited;
    const appVersion = getVersion();
    const hasMorethan30Secs = (item?.actionData?.content?.duration ?? 0) > 30;
    const size = `${height.value}x300`;
    const adTagUrlRef = useRef<string>();
    const isAutoplayExpandRef = useRef(false);
    const setLongVideoSelected = useFeedStore((state) => state.setLongVideoSelected);
    const { push } = useRouter();
    const { recommendedProducts, fetchRecommendedProducts } = useRecommendedProducts();
    const { t } = useTranslation();

    if (hasMorethan30Secs) {
      adTagUrlRef.current = `${getKisaanBaseURL()}/ups/v1/tools/${toolId()}/devices/${uniqueDeviceId}/vast-ads?placement=nkvideoplayerpage&deviceAdId=${adId}&deviceIsLat=${isAdTrackingLimited}&size=${size}&source_video_id=${item?.actionData?.content?.id ?? ''}&source_video_length=${item?.actionData?.content?.duration ?? 0}&last_ad_played_at=1724751118&appVersion=${appVersion}&delay_in_secs=30`;
    }

    if (item?.actionData?.content?.id !== lastItemId.current) {
      // console.log('Calling reset on vars!!!');
      lastItemId.current = item?.actionData?.content?.id;
      height.value = videoHeight;
      scale.value = 1;
      setIsExpanded(false);
      isAutoplayExpandRef.current = false;
    }

    useImperativeHandle(ref, () => ({
      resetAutoPlay: () => {
        // console.log('Reset auto play called!!!');
        height.value = videoHeight;
        scale.value = 1;
        setActiveFeedItemIndex?.(-1);
        setIsExpanded(false);
      },
    }));

    const animatedStyle = useAnimatedStyle(
      () => ({ height: height.value, transform: [{ scale: scale.value }] }),
      [height, scale, isVideoFeedActive]
    );

    const onAutoPlayModeExpandCallback = (isExpanded) => {
      console.log('Setting is expanded on list item => ', isExpanded);
      setIsExpanded(isExpanded);
    };

    useEffect(() => {
      const videoId = item?.actionData?.content?.id;
      if (videoId && index > 10) {
        fetchRecommendedProducts(videoId, 'stream');
      }
    }, [item?.actionData?.content?.id]);

    useEffect(() => {
      const prevIsExpanded = isAutoplayExpandRef.current;
      if (isExpanded && !prevIsExpanded) {
        isAutoplayExpandRef.current = true;
        // console.log('Animating height!!!');
        height.value = withTiming(isExpanded ? videoHeight : videoHeight, {
          duration: 300,
        });
        scale.value = withTiming(isExpanded ? 1 : 1.05, { duration: 300 });
        handleOnAutoPlayExpand?.(isExpanded, index, item?.actionData?.content?.id);
      }
      if (!isExpanded && prevIsExpanded) {
        // setIsVideoFeedActive(false);
        // setActiveFeedItemIndex?.(-1);
        isAutoplayExpandRef.current = false;
        const autoPlayVideoShorternEvent: INinjaEvent = {
          name: KissanEventType.AUTOPLAY_VIDEO_SHORTEN,
          screenDetails: { screen: 'feed page' },
          eventParams: [
            addEventParam('videoId', content?.id),
            addEventParam('videoDuration', content?.itemIndex),
          ],
        };
        enqueue(autoPlayVideoShorternEvent);
      }
    }, [isExpanded]);

    const thumbnail = useRef(item?.actionData?.mediaDetails?.[0]?.preview_url);

    const onAdSizeChange = (adHeight: number, width: number) => {
      // heightAd.value = withTiming(isExpanded ? adHeight : 0, { duration: 300 });
      // scaleAd.value = withTiming(isExpanded ? 1 : 0, { duration: 300 });
      const needsHeight = videoHeight + adHeight + 8;
      // height.value = withTiming(isExpanded ? needsHeight : videoHeight, { duration: 300 });
      height.value = isExpanded
        ? withTiming(needsHeight, { duration: 300 })
        : withTiming(videoHeight, { duration: 150 });
    };

    const onFeedItemPress = (event: GestureResponderEvent): void => {
      // if (bannerAdVisible || isAdActive) {
      //   console.log('Video or banner ad is active');
      //   event?.stopPropagation();
      //   return;
      // }
      console.log('On feed item clicked!!!');
      const additionalDetails = [
        addEventParam('itemId', item.actionData?.content?.id),
        addEventParam('postOwnerId', item.actionData?.content?.postOwnerId),
        addEventParam('mediaURL', item?.actionData?.mediaDetails[0]?.mediaUrl),
        addEventParam('videoId', item.actionData?.content?.id),
        addEventParam('recommendationId', item.actionData?.content?.recommendationId),
        addEventParam('itemIndex', item.actionData?.content?.itemIndex),
        addEventParam('eventId', item.actionData?.content?.eventId ?? ''),
        addEventParam('referenceKey4', 'rankingId'),
        addEventParam('referenceValue4', item?.actionData?.content?.rankingId),
      ];
      const eventToEnqueue: INinjaEvent = {
        name: pageName ? KissanEventType.PROFILE_OTHERS_ITEM_CLICK : KissanEventType.ITEM_CLICKED,
        screenDetails: { screen: 'feed video page' },
        eventParams: additionalDetails,
      };

      enqueue(eventToEnqueue);

      let videoPlayerTime = {
        userHistory: { currentTime: currentInfoRef?.current?.currentTime ?? 0 },
      };

      const adaptiveActionFormat: IAdaptiveActionCard = {
        ...item,
        actionData: {
          ...item.actionData,
          contentType: 'json',
          content: { ...item.actionData.content, ...videoPlayerTime },
          mediaDetails: item.actionData.mediaDetails || [],
          props: item.actionData.props || [],
          eventNamePrefix: item.actionData.eventNamePrefix || '',
        },
      };
      setLongVideoSelected(adaptiveActionFormat);
      push(`/longVideo/${item.actionData?.content?.id}?adDelay=true`);
    };

    // const onVideoMuted = (isMuted?: boolean) => {
    //   // setIsVideoMuted(isMuted ?? false);
    // };

    console.log('Rendering list item mute => ', isVideoMuted);

    // if (!isExpanded) {
    //   console.log('Not showing header!!!');
    // }
    return (
      <>
        <View backgroundColor="$cardbackground">
          <AnimatePresence>
            {/* {!isExpanded && (
              <YStack
                animation={'fadeIn'}
                enterStyle={{ opacity: 0, scale: 1 }}
                exitStyle={{ opacity: 0, scale: 0 }}
              > */}
            <ListHeader
              content={content}
              onReportBottomSheetOpen={onReportBottomSheetOpen}
              index={index}
              pageName={pageName}
              updateConnectionStatus={updateConnectionStatus}
            />
            {/* </YStack>
            )} */}
          </AnimatePresence>
          <Link
            href={`/watch?v=${item?.actionData?.content?.id}`}
            shallow
          >
            <View flex={1}>
              <View flex={1}>
                {!pageName && (
                  <Animated.View style={[animatedStyle, { flex: 1 }]}>
                    <Animated.View style={[animatedStyle]}>
                      <FeedVideoCard
                        item={item}
                        index={index}
                        isVideoFeedActive={isVideoFeedActive}
                        duration={item?.actionData?.content?.duration}
                        currentInfoRef={currentInfoRef}
                        onAutoPlayModeExpandCallback={onAutoPlayModeExpandCallback}
                        height={height}
                        adTagurl={adTagUrlRef.current}
                        pageName={pageName}
                        deviceWidth={deviceWidth}
                        isExpanded={isExpanded}
                        isAutoPlayBannerAdEnabled={isAutoPlayBannerAdEnabled}
                        setLongVideoSelected={setLongVideoSelected}
                        videoHeight={videoHeight}
                        // onVideoMuted={onVideoMuted}
                      />

                      {isExpanded && (
                        <XStack marginTop={10}>
                          <BannerAds
                            deviceWidth={deviceWidth ?? 0}
                            itemKey={`home-gpt-ad-expanded-${index.toString()}`}
                            unitId={adUnits.homepageActiveAdUnitId}
                            adSizeDefs={item?.actionData?.content?.adSizeDefs ?? ['320x50']}
                            adsPreloadData={adSizeDefsPreloaded}
                            bannerAdKeywords={bannerAdKeywords}
                            onAdSizeChange={onAdSizeChange}
                            adgebraShow={false}
                            screenName={'feed page'}
                            webAdSizeDefs={[[300, 50]]}
                          />
                        </XStack>
                      )}
                    </Animated.View>
                  </Animated.View>
                )}
              </View>
            </View>
          </Link>
          <ListTextContent content={content} maxLength={maxLength} />

          {recommendedProducts?.length ? (
            <>
              <View flex={1} padding={10}>
                <RecommendedProductsNudgeComponent
                  products={recommendedProducts}
                  t={t}
                  sourcePage={'home Page'}
                />
              </View>
            </>
          ) : null}

          {!pageName && (
            <>
              <Stack
                borderTopColor={'$feedBorderLineColor'}
                borderTopWidth={1}
                marginHorizontal={8}
              ></Stack>

              <ListFooter
                content={content}
                itemType={item.type}
                onCommentBottomSheetOpen={onCommentBottomSheetOpen}
                index={index}
                userProfile={userProfile!}
                uniqueDeviceId={uniqueDeviceId}
              />
            </>
          )}
        </View>

        {selectedImageIndex !== null && (
          <ImagePreview
            open={selectedImageIndex !== null}
            setOpen={setSelectedImageIndex}
            index={selectedImageIndex}
            imageUrls={item?.actionData?.mediaDetails}
            onClose={() => setSelectedImageIndex(null)}
          />
        )}
      </>
    );
  }
);
